import React, { useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import DnaWelcome from 'on_web/components/onboarding/dna/DnaWelcome';
import DnaStep1 from 'on_web/components/onboarding/dna/DnaStep1';
import DnaProviderSelection from 'on_web/components/onboarding/dna/DnaProviderSelection';
import DnaPayment from 'on_web/components/onboarding/dna/DnaPayment';
import DnaUploadFile from 'on_web/components/onboarding/dna/DnaUploadFile';
import DnaProcessing from 'on_web/components/onboarding/dna/DnaProcessing';
import DnaPurchaseDnaKit from 'on_web/components/onboarding/dna/DnaPurchaseDnaKit';

import PageItemContainer from 'on_web/components/shared/Common/PageItemContainer/PageItemContainer';

import {
  ROUTE_ASSESSMENT_HOME,
  ROUTE_ONBOARDING_GENERAL,
  ROUTE_ONBOARDING_DNA_INSTRUCTIONS,
} from 'routes';
import {
  userCanSeeDnaContent,
  isImputationInError,
  isGenotypeInvalid,
} from 'on_web/helpers/risksHelper';
import { getRedirectRoute } from '../helpers/redirectHelper';

import useUsersApi from '../hooks/useUsersApi';
import UserProfileContext from 'on_web/context/userProfileContext';

function OnboardingDna(props) {
  const context = React.useContext(UserProfileContext);
  const [visibleStep, setVisibleStep] = useState(
    props.location.state ? null : 1
  );
  const [selectedFile, setSelectedFile] = useState();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccessful, setUploadSuccessful] = useState(undefined);
  const [userGroups, setUserGroups] = useState(undefined);
  const [userMetadata, setUserMetadata] = useState(undefined);
  const [genotypeData, setGenotypeData] = useState(undefined);
  const [userEmail, setUserEmail] = useState(
    window.localStorage.getItem('email_address')
  );
  const [returningVisit] = useState(
    props.location &&
      props.location.state &&
      props.location.state.returningVisit
  );
  const [
    {
      response,
      loading,
      getUserData,
      saveUserData,
      errorSaving,
      getGenotype,
      saveGenotype,
      deleteGenotype,
      createEmailNotification,
    },
  ] = useUsersApi();

  useEffect(() => {
    getUserData().then(userData => {
      if (userData !== null) {
        if (userData.groups) {
          setUserGroups(userData.groups);
        }

        if (
          (visibleStep == null && props.location.state.startFromLast) ||
          userData.user_metadata.wantsToUseDna == null
        ) {
          return setVisibleStep(1);
        } else if (userData.user_metadata.wantsToUseDna === '1') {
          if (userData.user_metadata.dnaUsageOption === '1') {
            if (userData.user_metadata.dnaProviders == null) {
              setVisibleStep(2);
            } else {
              if (
                userData.groups &&
                userData.groups.find(
                  group => group === 'spare-dna-inapp-purchase-users'
                )
              ) {
                setVisibleStep(4);
              } else {
                setVisibleStep(3);
              }
            }
          } else if (
            userData.user_metadata.dnaUsageOption === '2' &&
            userData.user_metadata.dnaKitInstructionsStep !== 6
          ) {
            props.history.push(ROUTE_ONBOARDING_DNA_INSTRUCTIONS);
          } else if (userData.user_metadata.dnaUsageOption === '3') {
            setVisibleStep(6);
          } else if (response != null) {
            const route = getRedirectRoute(
              response.user_metadata,
              context.showBasicQuestions
            );
            props.history.push(route);
          }
        }
      }
    });
  }, [getUserData]);

  useEffect(() => {
    getGenotype().then(response => {
      if (response && response.status) {
        setGenotypeData({
          genotype_status: response.status,
          imputed_genotype_status: response.imputed_genotype
            ? response.imputed_genotype.status
            : null,
        });
      }
    });
  }, [getGenotype]);

  useEffect(() => {
    getUserData().then(resp => {
      if (resp !== null) {
        if (resp.groups !== null) {
          setUserGroups(resp.groups);
        }
        if (resp.user_metadata !== null) {
          setUserMetadata(resp.user_metadata);
        }
      }
    });
  }, [getUserData]);

  useEffect(() => {
    if (
      userGroups !== undefined &&
      userMetadata !== undefined &&
      genotypeData !== undefined
    ) {
      if (
        userGroups.find(group => group === 'spare-dna-inapp-purchase-users') &&
        genotypeData.genotype_status !== null &&
        !isImputationInError(genotypeData) &&
        !isGenotypeInvalid(genotypeData)
      ) {
        props.history.push(ROUTE_ASSESSMENT_HOME);
      } else {
        if (userCanSeeDnaContent(userGroups) === false) {
          if (userMetadata.dnaProviders == null) {
            setVisibleStep(1);
          } else {
            if (
              userGroups.find(
                group => group === 'spare-dna-inapp-purchase-users'
              )
            ) {
              setVisibleStep(4);
            } else {
              setVisibleStep(3);
            }
          }
        } else {
          if (visibleStep == null && props.location.state.startFromLast) {
            return setVisibleStep(1);
          } else if (userMetadata.wantsToUseDna == null) {
            setVisibleStep(0);
          } else if (userMetadata.wantsToUseDna === '1') {
            if (userMetadata.dnaUsageOption === '1') {
              if (userMetadata.dnaProviders == null) {
                setVisibleStep(2);
              } else {
                setVisibleStep(4);
              }
            } else if (
              userMetadata.dnaUsageOption === '2' &&
              userMetadata.dnaKitInstructionsStep !== 6
            ) {
              props.history.push(ROUTE_ONBOARDING_DNA_INSTRUCTIONS);
            } else if (userMetadata.dnaUsageOption === '3') {
              setVisibleStep(6);
            } else if (response != null) {
              const route = getRedirectRoute(
                response.user_metadata,
                context.showBasicQuestions
              );
              props.history.push(route);
            }
          }
        }
      }
    }
  }, [genotypeData, userMetadata, userGroups]);

  const screenShouldBeVisible = screenIndex => {
    return visibleStep === screenIndex;
  };

  const onWelcome = () => {
    setVisibleStep(visibleStep + 1);
  };

  const handleNextClick = userMetaData => {
    response.user_metadata = userMetaData;
    saveUserData(response);
    setVisibleStep(visibleStep + 1);
  };

  const handleStep1Payload = userMetadata => {
    response.user_metadata = userMetadata;
    if (userMetadata.wantsToUseDna === '1') {
      saveUserData(response).then(() => {
        if (userMetadata.dnaUsageOption === '1') {
          setVisibleStep(2);
        } else if (userMetadata.dnaUsageOption === '2') {
          props.history.push(ROUTE_ONBOARDING_DNA_INSTRUCTIONS);
        } else if (userMetadata.dnaUsageOption === '3') {
          setVisibleStep(6);
        }
      });
    } else {
      response.user_metadata.onboarding = {
        ...response.user_metadata.onboarding,
        dnaFinished: true,
      };
      saveUserData(response).then(() => {
        if (errorSaving == null) {
          props.history.push(ROUTE_ONBOARDING_GENERAL);
        }
      });
    }
  };

  const onFileUpload = file => {
    setSelectedFile(file);
    setVisibleStep(visibleStep + 1);

    const params = {
      fileupload: {
        content_type: 'application/octet-stream',
        method: 'PUT',
        original_filename: file.name,
      },
    };
    deleteGenotype()
      .then(response => {
        return response;
      })
      .then(() => {
        return saveGenotype(params);
      })
      .then(() => {
        return getGenotype();
      })
      .then(resp => {
        const uploadUrl = resp.source_details.upload_url;

        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);

        xhr.upload.onprogress = ev => {
          setUploadProgress((ev.loaded * 100) / ev.total);
        };

        let emailNotificationData = {
          event_type: 'genotype-processed',
          email_template: 'spare-genotype-ready',
          recipient_email: userEmail,
        };

        xhr.onload = () => {
          const status = xhr.status;
          if (status === 200) {
            setUploadSuccessful(true);
            setUploadProgress(100);
            //TODO (acengic) When whe get the screen for entering email, handle case when there is no email,
            if (userEmail) {
              createEmailNotification(emailNotificationData);
            }
          }
        };

        xhr.onerror = err => {
          console.log(err);
          setUploadSuccessful(false);
          setUploadProgress(100);
        };
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.send(file);
      });
  };

  return (
    <div>
      <Grid
        container={useMediaQuery('(min-width:1000px)')}
        direction="row"
        justify="center"
        alignItems="center"
      >
        {loading === false && response && (
          <UserProfileContext.Provider
            value={{
              ...response,
              showBasicQuestions:
                context && context.showBasicQuestions === true
                  ? context.showBasicQuestions
                  : false,
              fileName: selectedFile ? selectedFile.name : '',
            }}
          >
            {screenShouldBeVisible(0) && (
              <PageItemContainer>
                <DnaWelcome onWelcome={() => onWelcome()} />
              </PageItemContainer>
            )}
            {screenShouldBeVisible(1) && (
              <PageItemContainer>
                <DnaStep1
                  userGroups={userGroups}
                  onClickNext={payload => handleStep1Payload(payload)}
                  onClickBack={() =>
                    props.history.push({
                      pathname: ROUTE_ASSESSMENT_HOME,
                      state: {
                        startFromLast: true,
                      },
                    })
                  }
                />
              </PageItemContainer>
            )}
            {screenShouldBeVisible(2) && (
              <PageItemContainer>
                <DnaProviderSelection
                  onClickNext={userMetaData => handleNextClick(userMetaData)}
                  onClickBack={() => setVisibleStep(visibleStep - 1)}
                />
              </PageItemContainer>
            )}
            {(screenShouldBeVisible(3) || screenShouldBeVisible(7)) && (
              <PageItemContainer>
                <DnaPayment
                  onClickNext={userMetaData => handleNextClick(userMetaData)}
                  onClickBack={() => {
                    if (response.user_metadata.dnaUsageOption === '1') {
                      setVisibleStep(2);
                    } else {
                      setVisibleStep(6);
                    }
                  }}
                />
              </PageItemContainer>
            )}
            {screenShouldBeVisible(4) && (
              <PageItemContainer>
                <DnaUploadFile
                  onClickNext={e => onFileUpload(e)}
                  onClickBack={() => {
                    setVisibleStep(3);
                  }}
                  returningVisit={returningVisit}
                />
              </PageItemContainer>
            )}
            {screenShouldBeVisible(5) && (
              <PageItemContainer>
                <DnaProcessing
                  selectedFile={selectedFile}
                  progress={uploadProgress}
                  uploadSuccessful={uploadSuccessful}
                  onClickNext={() => props.history.push(ROUTE_ASSESSMENT_HOME)}
                  onClickBack={() => setVisibleStep(4)}
                />
              </PageItemContainer>
            )}
            {screenShouldBeVisible(6) && (
              <PageItemContainer>
                <DnaPurchaseDnaKit
                  onClickNext={() => setVisibleStep(visibleStep - 3)}
                  onClickBack={() => setVisibleStep(1)}
                />
              </PageItemContainer>
            )}
          </UserProfileContext.Provider>
        )}
      </Grid>
    </div>
  );
}

export default OnboardingDna;
